import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, ViewChild, OnDestroy, ViewContainerRef, ComponentRef, inject, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FuseDrawerModule, FuseDrawerComponent } from '@fuse/components/drawer';
import { FuseDrawerMode, FuseDrawerPosition } from '@fuse/components/drawer/drawer.types';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { DrawerHeaderComponent } from './drawer-header.component';
import { CIPXDrawerTemplateComponent } from './drawer-template.component';
import { CIPXDrawerContentInterface } from './drawer-content.interface';

@Component({
  selector: 'cipx-drawer',
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
  imports: [
    CommonModule,
    FuseDrawerModule,
    MatIcon,
    MatIconButton,
    DrawerHeaderComponent,
    CIPXDrawerTemplateComponent
  ],
  standalone: true,
})
export class CIPXDrawerComponent implements OnDestroy {
  @ViewChild('drawerTemplate', { read: CIPXDrawerTemplateComponent }) drawerTemplate!: CIPXDrawerTemplateComponent;

  // Drawer configuration
  @Input() fixed = true;
  @Input() sizeType: 'small' | 'half' | 'large' = 'half';
  @Input() showUpBand = true;
  @Input() mode: FuseDrawerMode = 'over';
  @Input() name = `drawer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  @Input() opened = false;
  @Input() position: FuseDrawerPosition = 'right';
  @Input() transparentOverlay = true;
  @Input() disableOverlayClose = true;
  @Input() disableOverlay = true;
  @Input() showCloseButton = false;

  // Header configuration
  @Input() title = '';
  @Input() showHeader = true;
  @Input() showBackButton = false;

  // Events
  @Output() openedChanged = new EventEmitter<boolean>();
  @Output() backButtonClicked = new EventEmitter<void>();
  @Output() closeRequested = new EventEmitter<void>();

  // Content component reference - now typed with interface
  private contentComponentRef?: ComponentRef<CIPXDrawerContentInterface<any>>;

  // Track if view is initialized
  private viewInitialized = false;

  // Pending content to set after view init
  private pendingContent?: ComponentRef<CIPXDrawerContentInterface<any>>;

  // Change detector
  private cdr = inject(ChangeDetectorRef);

  // Access to underlying FuseDrawer and ViewContainer through template component
  get fuseDrawer(): FuseDrawerComponent {
    return this.drawerTemplate?.fuseDrawer;
  }

  get drawerContent(): ViewContainerRef {
    return this.drawerTemplate?.drawerContent;
  }

  constructor(private elementRef: ElementRef) {}

  onTemplateViewInit() {
    // Mark view as initialized
    this.viewInitialized = true;

    // If there's pending content, set it now
    if (this.pendingContent) {
      this.setContent(this.pendingContent);
      this.pendingContent = undefined;
    }

    // Register with drawer service if available
    this.registerWithService();
  }

  ngOnDestroy() {
    // Call beforeDrawerClosed on content component
    if (this.contentComponentRef?.instance?.beforeDrawerClosed) {
      this.contentComponentRef.instance.beforeDrawerClosed();
    }

    // Unregister from service
    this.unregisterFromService();

    // Clean up content component
    if (this.contentComponentRef) {
      this.contentComponentRef.destroy();
    }
  }

  // Set content component dynamically
  setContent(componentRef: ComponentRef<CIPXDrawerContentInterface<any>>) {
    // If view is not initialized yet, store for later
    if (!this.viewInitialized || !this.drawerContent) {
      this.pendingContent = componentRef;
      // Force change detection to ensure view initializes
      this.cdr.detectChanges();
      return;
    }

    // Clear existing content
    this.drawerContent.clear();

    // Store reference
    this.contentComponentRef = componentRef;

    // Insert the component
    this.drawerContent.insert(componentRef.hostView);

    // Listen for close events from content
    if (componentRef.instance.closeRequested) {
      componentRef.instance.closeRequested.subscribe(() => {
        this.close();
      });
    }

    // Force change detection
    this.cdr.detectChanges();
  }

  // Methods to control the drawer
  open(): void {
    if (this.fuseDrawer) {
      this.fuseDrawer.open();

      // Call afterDrawerOpened on content component
      if (this.contentComponentRef?.instance?.afterDrawerOpened) {
        setTimeout(() => {
          this.contentComponentRef!.instance.afterDrawerOpened!();
        }, 400);
      }
    }
  }

  async close(): Promise<void> {
    // Check if content component allows closing
    if (this.contentComponentRef?.instance?.canClose) {
      const canClose = await this.contentComponentRef.instance.canClose();
      if (!canClose) {
        return;
      }
    }

    // Call beforeDrawerClosed on content component
    if (this.contentComponentRef?.instance?.beforeDrawerClosed) {
      this.contentComponentRef.instance.beforeDrawerClosed();
    }

    if (this.fuseDrawer) {
      this.fuseDrawer.close();
    }
    this.closeRequested.emit();
  }

  toggle(): void {
    if (this.fuseDrawer) {
      this.fuseDrawer.toggle();
    }
  }

  // Event handlers
  onOpenedChanged(opened: boolean): void {
    this.opened = opened;
    this.openedChanged.emit(opened);
  }

  onBackClick(): void {
    this.backButtonClicked.emit();

    // Also emit to content component if it has a handler
    if (this.contentComponentRef?.instance?.onBackClick) {
      this.contentComponentRef.instance.onBackClick();
    }
  }

  onCloseClick(): void {
    this.close();
  }

  // Service registration methods
  private registerWithService(): void {
    // TODO: Implement when drawer service is available
  }

  private unregisterFromService(): void {
    // TODO: Implement when drawer service is available
  }
}
